import { useEffect, useLayoutEffect, useRef } from 'react'
import { ScreenInit } from './components/Player/ScreenManager'
import { Screen } from 'common/types'

export { unwrapChannelPane, unwrapPlaylist } from 'common/utils'

export function useTimeout(callback: () => void, delay: number | null) {
  const savedCallback = useRef(callback)

  // Remember the latest callback if it changes.
  useLayoutEffect(() => {
    savedCallback.current = callback
  }, [callback])

  // Set up the timeout.
  useEffect(() => {
    // Don't schedule if no delay is specified.
    // Note: 0 is a valid value for delay.
    if (!delay && delay !== 0) {
      return
    }

    const id = setTimeout(() => savedCallback.current(), delay)

    return () => clearTimeout(id)
  }, [delay])
}

export function reload() {
  window.location.reload()
}

export function sum(values: number[]) {
  return values.reduce((a, b) => a + b, 0)
}

const now = new Date()

export function reloadAppDaily() {
  const midnight = new Date()
  midnight.setHours(24, 0, 0, 0)
  const timeLeft = midnight.getTime() - now.getTime()

  console.log(`Will reload app ${timeLeft / 3600_000} hours`)

  setTimeout(() => {
    reload()
  }, timeLeft)
}

/**
 * TVs and sticks blank the panel on their own idle timer, and a signage page has no
 * user input to reset it. The Screen Wake Lock API holds the panel on where it exists
 * (Chrome-based browsers, Android WebView); it is absent on Tizen/webOS TV browsers and
 * on those the native app or the TV's own screensaver setting is the only lever.
 * The lock is dropped by the platform whenever the page is hidden, so it is re-acquired
 * on every return to visible.
 */
export function keepScreenAwake() {
  if (!('wakeLock' in navigator)) {
    console.log('Wake Lock API is not available')
    return
  }

  let sentinel: WakeLockSentinel | null = null

  const acquire = async () => {
    if (sentinel || document.visibilityState !== 'visible') return
    try {
      sentinel = await navigator.wakeLock.request('screen')
      sentinel.addEventListener('release', () => {
        sentinel = null
      })
      console.log('Screen wake lock acquired')
    } catch (err) {
      console.warn('Screen wake lock request failed', err)
    }
  }

  document.addEventListener('visibilitychange', () => {
    acquire()
  })

  acquire()
}

export function isNewScreen(screen: Screen | ScreenInit) {
  return screen.id === undefined
}

export function sleep(ms: number) {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

export function isLocalhost() {
  return window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
}
